import type {
	ValueEditorType as BuilderValueEditorType,
	ValueType as BuilderValueType,
} from '@/shared/components/query-builder'
import type {
	FieldSchemaModel,
	FilterRuleModel,
	ReferenceFilterRule,
} from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { FilterField } from '../model/types'

import { format } from 'date-fns'

import { Operators } from '@/shared/components/query-builder'
import { ValueEditorType, ValueType } from '@/shared/generated'
import { DateFormat, getUTCDate } from '@/shared/tools/dates'
import { asNumber } from '@/shared/tools/utils'

const isValidDate = (date: string) => {
	const parsedDate = new Date(date)

	return !isNaN(parsedDate.getTime())
}
/**
 * Transform column schema to filter field model
 * @param schema  - column schema model
 * @returns  - adapted filter field
 */
export const toAdaptedSchema = (
	schema: FieldSchemaModel,
	t: TFunction
): FilterField => {
	const value = schema.id
	const valueType = schema.value_type as unknown as BuilderValueType
	const valueEditorType = schema.value_editor_type
		? (schema.value_editor_type as unknown as BuilderValueEditorType)
		: undefined
	const label = t(schema.name, { ns: 'fields', defaultValue: schema.name })

	return {
		value,
		label,
		valueType,
		valueEditorType,
		enum: schema.config?.enum || [],
		note: schema.description
			? t(schema.description, {
					ns: 'fields',
					defaultValue: schema.description,
				})
			: '',
		...getEditorProps(schema),
	}
}
/**
 * Get filter field props by column type name
 * @param type  - column type
 * @returns  - filter field props
 */
export const getEditorProps = (
	schema: FieldSchemaModel
):
	| Pick<FilterField, 'valueType' | 'valueEditorType' | 'operations'>
	| object => {
	if (
		!!schema.value_editor_type &&
		schema.value_editor_type === ValueEditorType.Multiselect
	) {
		const fragment = {
			valueEditorType: ValueEditorType.Multiselect,
		}
		if (schema.value_type === ValueType.String) {
			return {
				...fragment,
				operations: [
					Operators.IS,
					Operators.IS_NOT,
					Operators.IN,
					Operators.NOT_IN,
					Operators.IS_EMPTY,
					Operators.IS_NOT_EMPTY,
				],
			}
		}
		if (schema.value_type === ValueType.Array) {
			return {
				...fragment,
				operations: [
					Operators.ANY_OF,
					Operators.IS_EMPTY,
					Operators.IS_NOT_EMPTY,
				],
			}
		}

		return fragment
	} else if (
		schema.value_type === ValueType.Array &&
		!schema.value_editor_type
	) {
		return {
			operations: [
				Operators.ANY_OF,
				Operators.IS_EMPTY,
				Operators.IS_NOT_EMPTY,
			],
			valueEditorType: ValueEditorType.Multiselect,
		}
	} else {
		return {}
	}
}
const isRef = (rule: FilterRuleModel): rule is ReferenceFilterRule =>
	'reference' in rule

const isNumericOperation = (operation: Operators) =>
	[
		Operators.GT,
		Operators.GTE,
		Operators.LTE,
		Operators.LT,
		Operators.EQ,
		Operators.NE,
		Operators.BETWEEN,
	].includes(operation)

const isDateOperation = (operation: Operators) =>
	[
		Operators.DATE_AFTER,
		Operators.DATE_AT,
		Operators.DATE_BEFORE,
		Operators.DATE_BETWEEN,
	].includes(operation)

const isStringOperation = (operation: Operators) =>
	[
		Operators.IS,
		Operators.IS_NOT,
		Operators.CONTAINS,
		Operators.NOT_CONTAINS,
		Operators.IN,
		Operators.NOT_IN,
	].includes(operation)

const isArrayOperation = (operation: Operators) =>
	[
		Operators.ANY_OF,
		Operators.ALL_OF,
		Operators.EXACT,
		Operators.NONE_OF,
	].includes(operation)
export const formatFilterRule = (rule: FilterRuleModel) => {
	if (isRef(rule)) {
		return rule
	}
	if (isDateOperation(rule.operation as Operators)) {
		if (Array.isArray(rule.value)) {
			return {
				...rule,
				value: rule.value
					.filter((date) => date && isValidDate(date as string))
					.map((date) =>
						format(getUTCDate(new Date(date as string)), DateFormat.system)
					),
			} as FilterRuleModel
		} else {
			return {
				...rule,
				value: isValidDate(rule.value as string)
					? format(
							getUTCDate(new Date(rule.value as string)),
							DateFormat.system
						)
					: undefined,
			} as FilterRuleModel
		}
	} else if (isNumericOperation(rule.operation as Operators)) {
		if (Array.isArray(rule.value)) {
			return {
				...rule,
				value: rule.value.map((num) => asNumber(num as string) as number),
			} as FilterRuleModel
		} else {
			return {
				...rule,
				value: asNumber(rule.value as string) as number,
			} as FilterRuleModel
		}
	} /* else if (isStringOperation(rule.operation as Operators)) {
		if (Array.isArray(rule.value)) {
			return {
				...rule,
				value: rule.value.map((item) => {
					if (typeof item === 'string') {
						return item.trim()
					}

					return item
				}) as string[],
			}
		} else {
			return {
				...rule,
				value: rule.value.trim() as string,
			} as FilterRuleModel
		}
	} */

	return rule as FilterRuleModel
}
